/**
 * The undo/redo stack. One instance per open editor session (the editor
 * screen owns it); NOT global state — closing the editor drops history.
 *
 * `subscribe` exists so UI (undo/redo buttons) can mirror `canUndo`/
 * `canRedo` reactively without putting the stack itself in React state.
 *
 * @module floorplan/history/CommandStack
 * @category FloorPlan
 */

import { MAX_HISTORY_DEPTH } from '../constants';
import type { CommandTarget, FloorCommand } from './types';

type HistoryListener = () => void;

export class CommandStack {
  private undoStack: FloorCommand[] = [];

  private redoStack: FloorCommand[] = [];

  private listeners = new Set<HistoryListener>();

  private readonly maxDepth: number;

  constructor(maxDepth: number = MAX_HISTORY_DEPTH) {
    this.maxDepth = maxDepth;
  }

  /** Execute a command and push it (coalescing via `merge` when offered). */
  execute(command: FloorCommand, target: CommandTarget): void {
    command.do(target);
    this.push(command);
  }

  /**
   * Record a command WITHOUT executing it — for interactions where the
   * mutation already happened live (a drag committed on gesture end) and
   * only the undo record is needed.
   */
  push(command: FloorCommand): void {
    const top = this.undoStack[this.undoStack.length - 1];
    if (top !== undefined && top.merge !== undefined) {
      const merged = top.merge(command);
      if (merged !== null) {
        this.undoStack[this.undoStack.length - 1] = merged;
        this.redoStack = [];
        this.emit();
        return;
      }
    }
    this.undoStack.push(command);
    if (this.undoStack.length > this.maxDepth) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.emit();
  }

  undo(target: CommandTarget): boolean {
    const command = this.undoStack.pop();
    if (command === undefined) return false;
    command.undo(target);
    this.redoStack.push(command);
    this.emit();
    return true;
  }

  redo(target: CommandTarget): boolean {
    const command = this.redoStack.pop();
    if (command === undefined) return false;
    command.do(target);
    this.undoStack.push(command);
    this.emit();
    return true;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Label of the next undo step (history UI), or null. */
  get undoLabel(): string | null {
    const top = this.undoStack[this.undoStack.length - 1];
    return top === undefined ? null : top.label;
  }

  get redoLabel(): string | null {
    const top = this.redoStack[this.redoStack.length - 1];
    return top === undefined ? null : top.label;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.emit();
  }

  /** Subscribe to depth changes; returns the unsubscribe function. */
  subscribe(listener: HistoryListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
